import { iconList } from '../icons/RoleIconList';
import { BADGE_COLOURS, mix } from './badge-colours';
import { BADGE_SHAPES, TAIL_L, TAIL_R, digits, wreath } from './badge-geometry';

export type BadgeShape = keyof typeof BADGE_SHAPES;
export type BadgeFrame = 'none' | 'ring' | 'dots' | 'wreath';
export type BadgeCentre = 'number' | 'icon' | 'text' | 'logo';
export type BadgeFillStyle = 'solid' | 'soft' | 'outline';
export type BadgeLogoMode = 'match' | 'original';

export type BadgeLogo = { kind: 'svg'; vb: string; inner: string } | { kind: 'image'; src: string };

export interface BadgeDesign {
  shape: BadgeShape;
  frame: BadgeFrame;
  centre: BadgeCentre;
  value: string;
  unit: string;
  icon: string;
  text: string;
  logo: BadgeLogo | null;
  logoMode: BadgeLogoMode;
  colour: string;
  style: BadgeFillStyle;
  inlay: boolean;
  tails: boolean;
}

export interface BadgeRenderOptions {
  size?: number;
  label?: string;
  locked?: boolean;
}

export const DEFAULT_BADGE_DESIGN: BadgeDesign = {
  shape: 'rosette',
  frame: 'none',
  centre: 'number',
  value: '5',
  unit: '',
  icon: 'medal',
  text: '',
  logo: null,
  logoMode: 'match',
  colour: 'gold',
  style: 'solid',
  inlay: false,
  tails: false,
};

export const BADGE_FRAMES: Record<BadgeFrame, string> = { none: 'None', ring: 'Ring', dots: 'Dots', wreath: 'Wreath' };
export const BADGE_UNITS = ['', 'years', 'hours', 'shifts', 'events'];
export const BADGE_FONT = 'Outfit, system-ui, -apple-system, sans-serif';

const LOCKED_COLOUR = { name: 'Locked', base: '#C9C9C9', light: '#EFEFEF', dark: '#8A8A8A', pale: true };

let uid = 0;

export const escapeXml = (s: string) =>
  String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c] as string));

export const isTailsAvailable = (shape: BadgeShape) => shape !== 'square' && shape !== 'none';

const logoMarkup = (logo: BadgeLogo, mode: BadgeLogoMode, ink: string, cy: number, r: number, clipId: string) => {
  if (logo.kind === 'svg' && mode === 'match') {
    const inner = logo.inner.replace(/(fill|stroke)="(?!none)[^"]*"/g, `$1="${ink}"`);
    const w = r * 1.55;
    return `<svg x="${100 - w / 2}" y="${cy - w / 2}" width="${w}" height="${w}" viewBox="${
      logo.vb
    }" fill="${ink}">${inner}</svg>`;
  }
  const disc = `<circle cx="100" cy="${cy}" r="${r + 4}" fill="#FFFFFF"/>`;
  if (logo.kind === 'svg') {
    const w = r * 1.3;
    return `${disc}<svg x="${100 - w / 2}" y="${cy - w / 2}" width="${w}" height="${w}" viewBox="${logo.vb}">${
      logo.inner
    }</svg>`;
  }
  return `${disc}<image href="${escapeXml(logo.src)}" x="${100 - r}" y="${cy - r}" width="${r * 2}" height="${r *
    2}" clip-path="url(#${clipId})" preserveAspectRatio="xMidYMid slice"/>`;
};

export const badgeHeight = (s: BadgeDesign) =>
  s.tails && isTailsAvailable(s.shape) && s.frame !== 'wreath' ? 236 : 206;

export const badgeSVG = (s: BadgeDesign, opts: BadgeRenderOptions = {}) => {
  const id = `badge${uid++}`;
  const c = opts.locked ? LOCKED_COLOUR : BADGE_COLOURS[s.colour] || BADGE_COLOURS.gold;
  const grad = 'grad' in c ? c.grad : undefined;
  const compact = !!opts.size && opts.size < 48;
  const wreathOn = s.frame === 'wreath';
  const bare = s.shape === 'none';
  const tails = s.tails && isTailsAvailable(s.shape);
  const H = badgeHeight(s);
  const d = BADGE_SHAPES[s.shape].d || (BADGE_SHAPES.circle.d as string);
  const metal = !!grad;
  const style = opts.locked ? 'soft' : s.style;

  let fill: string;
  let ink: string;
  let frameInk: string;
  let edgeW = 0;
  let edge = 'none';
  if (style === 'solid') {
    fill = metal ? `url(#${id}g)` : c.base;
    ink = c.pale ? c.dark : '#FFFFFF';
    frameInk = c.pale ? mix(c.base, c.dark, 0.45) : 'rgba(255,255,255,.7)';
  } else if (style === 'soft') {
    fill = c.light;
    ink = c.dark;
    frameInk = c.base;
  } else {
    fill = '#FFFFFF';
    ink = c.dark;
    frameInk = c.base;
    edge = c.base;
    edgeW = 6;
  }
  const inlayFill =
    style === 'solid' ? mix(c.base, c.dark, 0.32) : style === 'soft' ? mix(c.light, c.base, 0.3) : c.light;
  if (bare) {
    ink = c.dark;
    frameInk = c.base;
  }

  const cy = 100 + (s.shape === 'shield' ? -9 : 0);
  const sizeAttrs = opts.size ? ` width="${opts.size}" height="${((opts.size * H) / 200).toFixed(1)}"` : '';
  let svg = `<svg viewBox="0 0 200 ${H}" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="${escapeXml(
    opts.label || ''
  )}"${sizeAttrs}>`;
  svg += `<defs><linearGradient id="${id}g" x1="0" y1="0" x2=".6" y2="1">${
    grad
      ? `<stop offset="0" stop-color="${grad[0]}"/><stop offset=".6" stop-color="${c.base}"/><stop offset="1" stop-color="${grad[1]}"/>`
      : ''
  }</linearGradient><clipPath id="${id}c"><circle cx="100" cy="${cy}" r="46"/></clipPath></defs>`;

  if (wreathOn) {
    svg += wreath(metal && style === 'solid' ? `url(#${id}g)` : c.pale ? mix(c.base, c.dark, 0.45) : c.base);
    svg += `<g transform="translate(100 96) scale(${bare ? 0.9 : 0.74}) translate(-100 -100)">`;
  } else {
    svg += '<g>';
  }

  const [tailA, tailB] =
    style === 'solid'
      ? [mix(c.base, c.dark, 0.6), mix(c.base, c.dark, 0.3)]
      : style === 'soft'
      ? [c.base, mix(c.light, c.base, 0.55)]
      : [c.dark, mix(c.base, c.dark, 0.3)];
  if (tails) svg += `<path d="${TAIL_L}" fill="${tailA}"/><path d="${TAIL_R}" fill="${tailB}"/>`;
  if (!bare) svg += `<path d="${d}" fill="${fill}" stroke="${edge}" stroke-width="${edgeW}" stroke-linejoin="round"/>`;
  const inlayOn = s.inlay && !bare;
  if (inlayOn)
    svg += `<path d="${d}" fill="${inlayFill}" transform="translate(100 100) scale(.84) translate(-100 -100)"/>`;

  const inset = (extra: string) =>
    `<path d="${d}" fill="none" stroke="${frameInk}" vector-effect="non-scaling-stroke" stroke-linecap="round" ${extra} transform="translate(100 100) scale(.84) translate(-100 -100)"/>`;
  if (s.frame === 'ring') svg += inset(`stroke-width="${opts.size ? Math.max(1.5, opts.size / 20) : 5}" opacity=".9"`);
  if (s.frame === 'dots' && !compact) {
    const w = opts.size ? Math.max(2, opts.size / 22) : 7;
    const gap = opts.size ? Math.max(4, opts.size / 9) : 15;
    svg += inset(`stroke-width="${w}" stroke-dasharray="0 ${gap}"`);
  }

  svg += inlayOn ? `<g transform="translate(100 ${cy}) scale(.84) translate(-100 ${-cy})">` : '<g>';
  if (s.centre === 'number') {
    const unit = !compact && s.unit;
    svg += digits(String(s.value || '').slice(0, 4), cy - (unit ? 12 : 0), unit ? 76 : 84, ink);
    if (unit) {
      svg += `<text x="100" y="${cy +
        26}" text-anchor="middle" dominant-baseline="central" font-family="${BADGE_FONT}" font-weight="600" font-size="20" fill="${ink}" opacity=".9">${escapeXml(
        s.unit
      )}</text>`;
    }
  } else if (s.centre === 'icon') {
    const k = 3.4;
    const half = 12 * k;
    const path = iconList[s.icon] || iconList.star;
    svg += `<g transform="translate(${100 - half} ${cy - half}) scale(${k})"><path d="${path}" fill="${ink}"/></g>`;
  } else if (s.centre === 'text') {
    const t = (s.text || '').toUpperCase().slice(0, 3);
    const size = [76, 76, 62, 48][t.length];
    svg += `<text x="100" y="${cy}" text-anchor="middle" dominant-baseline="central" font-family="${BADGE_FONT}" font-weight="600" font-size="${size}" fill="${ink}">${escapeXml(
      t
    )}</text>`;
  } else if (s.centre === 'logo' && s.logo) {
    svg += logoMarkup(s.logo, s.logoMode, ink, cy, 46, `${id}c`);
  }
  return `${svg}</g></g></svg>`;
};
